import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Plus, Edit, Trash2, Save, X, ArrowLeft, Loader2,
  CheckCircle, AlertCircle, ExternalLink, RefreshCw, Key,
  Settings, Eye, EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { correoAPI } from '../api';
import { toast } from '../hooks/use-toast';

const CODIGOS_CUENTA = [
  { value: 'gestion_info', label: 'Gestión Info', descripcion: 'Información general' },
  { value: 'gestion_expedientes', label: 'Gestión Expedientes', descripcion: 'Correos de expedientes' },
  { value: 'gestion_abogados', label: 'Gestión Abogados', descripcion: 'Tareas de abogados' },
];

const ConfiguracionCuentasCorreo = () => {
  const navigate = useNavigate();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    email: '',
    google_client_id: '',
    google_client_secret: '',
    google_redirect_uri: 'https://app.lexduo.cat/api/correo/oauth/callback'
  });

  useEffect(() => {
    loadCuentas();
  }, []);

  const loadCuentas = async () => {
    try {
      setLoading(true);
      const res = await correoAPI.getCuentas();
      setCuentas(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error al cargar cuentas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingCuenta(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      email: '',
      google_client_id: '',
      google_client_secret: '',
      google_redirect_uri: 'https://app.lexduo.cat/api/correo/oauth/callback'
    });
    setDialogOpen(true);
  };

  const handleEdit = (cuenta) => {
    setEditingCuenta(cuenta);
    setFormData({
      codigo: cuenta.codigo || '',
      nombre: cuenta.nombre || '',
      descripcion: cuenta.descripcion || '',
      email: cuenta.email || '',
      google_client_id: cuenta.google_client_id || '',
      google_client_secret: cuenta.google_client_secret || '',
      google_redirect_uri: cuenta.google_redirect_uri || 'https://app.lexduo.cat/api/correo/oauth/callback'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.nombre || !formData.email) {
      toast({ title: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      if (editingCuenta) {
        await correoAPI.actualizarCuenta(editingCuenta.id, formData);
        toast({ title: 'Cuenta actualizada correctamente' });
      } else {
        await correoAPI.crearCuenta(formData);
        toast({ title: 'Cuenta creada correctamente' });
      }
      setDialogOpen(false);
      loadCuentas();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'No se pudo guardar',
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cuenta) => {
    if (!window.confirm(`¿Eliminar la cuenta "${cuenta.nombre}"?`)) return;
    
    try {
      await correoAPI.eliminarCuenta(cuenta.id);
      toast({ title: 'Cuenta eliminada' });
      loadCuentas();
    } catch (error) {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const handleAutorizar = async (cuenta) => {
    try {
      const res = await correoAPI.iniciarAutorizacion(cuenta.id);
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'No se pudo iniciar autorización',
        variant: 'destructive' 
      });
    }
  };

  const handleRevocar = async (cuenta) => {
    if (!window.confirm('¿Revocar autorización de esta cuenta?')) return;
    
    try {
      await correoAPI.revocarAutorizacion(cuenta.id);
      toast({ title: 'Autorización revocada' });
      loadCuentas();
    } catch (error) {
      toast({ title: 'Error al revocar', variant: 'destructive' });
    }
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/configuracion')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail className="w-6 h-6 text-blue-600" />
              Cuentas de Correo Gmail
            </h1>
            <p className="text-gray-600 mt-1">
              Configura las cuentas de Gmail para el módulo de correo
            </p>
          </div>
        </div>
        <Button onClick={handleNew}>
          <Plus className="w-4 h-4 mr-2" />
          Nueva Cuenta
        </Button>
      </div>

      {/* Instrucciones */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <h3 className="font-semibold text-blue-800 mb-2">📋 Instrucciones de configuración</h3>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Cree un proyecto en <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
            <li>Habilite la <strong>Gmail API</strong></li>
            <li>Cree credenciales <strong>OAuth 2.0</strong> de tipo "Aplicación web"</li>
            <li>Añada como URI de redirección: <code className="bg-blue-100 px-1 rounded">https://app.lexduo.cat/api/correo/oauth/callback</code></li>
            <li>Añada como origen autorizado: <code className="bg-blue-100 px-1 rounded">https://app.lexduo.cat</code></li>
            <li>Copie el <strong>Client ID</strong> y <strong>Client Secret</strong> aquí</li>
          </ol>
        </CardContent>
      </Card>

      {/* Lista de cuentas */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : cuentas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No hay cuentas de correo configuradas</p>
            <Button onClick={handleNew}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primera cuenta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {cuentas.map((cuenta) => (
            <Card key={cuenta.id} className={cuenta.autorizado ? 'border-green-200' : 'border-yellow-200'}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{cuenta.nombre}</h3>
                      <Badge variant={cuenta.autorizado ? 'default' : 'secondary'} 
                             className={cuenta.autorizado ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                        {cuenta.autorizado ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Autorizada</>
                        ) : (
                          <><AlertCircle className="w-3 h-3 mr-1" /> Pendiente</>
                        )}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm mb-1">{cuenta.descripcion}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {cuenta.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Key className="w-4 h-4" />
                        Código: {cuenta.codigo}
                      </span>
                    </div>
                    {cuenta.google_client_id && (
                      <p className="text-xs text-gray-400 mt-2">
                        Client ID: {cuenta.google_client_id.substring(0, 30)}...
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!cuenta.autorizado ? (
                      <Button 
                        onClick={() => handleAutorizar(cuenta)}
                        disabled={!cuenta.google_client_id}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Autorizar con Google
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        onClick={() => handleRevocar(cuenta)}
                        className="text-orange-600 border-orange-300"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Revocar
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(cuenta)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(cuenta)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {editingCuenta ? 'Editar Cuenta de Correo' : 'Nueva Cuenta de Correo'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">Código de cuenta *</Label>
                <Select 
                  value={formData.codigo} 
                  onValueChange={(v) => {
                    const selected = CODIGOS_CUENTA.find(c => c.value === v);
                    setFormData({
                      ...formData, 
                      codigo: v,
                      nombre: selected?.label || formData.nombre
                    });
                  }}
                  disabled={!!editingCuenta}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CODIGOS_CUENTA.map((codigo) => (
                      <SelectItem key={codigo.value} value={codigo.value}>
                        {codigo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Ej: Gestión Abogados"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email de Gmail *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="cuenta@gmail.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                placeholder="Descripción de la cuenta..."
                rows={2}
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Key className="w-4 h-4" />
                Credenciales OAuth de Google
              </h4>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="client_id">Google Client ID</Label>
                  <div className="relative">
                    <Input
                      id="client_id"
                      type={showSecrets.client_id ? 'text' : 'password'}
                      value={formData.google_client_id}
                      onChange={(e) => setFormData({...formData, google_client_id: e.target.value})}
                      placeholder="XXXXX.apps.googleusercontent.com"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => toggleShowSecret('client_id')}
                    >
                      {showSecrets.client_id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_secret">Google Client Secret</Label>
                  <div className="relative">
                    <Input
                      id="client_secret"
                      type={showSecrets.client_secret ? 'text' : 'password'}
                      value={formData.google_client_secret}
                      onChange={(e) => setFormData({...formData, google_client_secret: e.target.value})}
                      placeholder="GOCSPX-XXXXX"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => toggleShowSecret('client_secret')}
                    >
                      {showSecrets.client_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="redirect_uri">URI de Redirección</Label>
                  <Input
                    id="redirect_uri"
                    value={formData.google_redirect_uri}
                    onChange={(e) => setFormData({...formData, google_redirect_uri: e.target.value})}
                    placeholder="https://app.lexduo.cat/api/correo/oauth/callback"
                  />
                  <p className="text-xs text-gray-500">
                    Esta URI debe estar configurada en Google Cloud Console
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionCuentasCorreo;
